import { motion } from "framer-motion";
import { Globe, MapPin, Zap, Sparkles, ScanLine, Sun } from "lucide-react";
import { IMAGES } from "@/lib/api";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const badges = [
  { icon: Globe, t: "International Brand", d: "Global benchmark in LED display technology" },
  { icon: MapPin, t: "First in Telangana & AP", d: "Brought to the Telugu states for the very first time" },
  { icon: Sparkles, t: "Premium Visuals", d: "True-to-life colour, brightness and clarity" },
];

const specs = [
  { icon: ScanLine, k: "P4.8", l: "Pixel Pitch", d: "4.8 mm fine-pitch for razor-sharp, seamless visuals" },
  { icon: Sun, k: "5000", l: "Nits Brightness", d: "Crystal-clear even under bright daylight" },
  { icon: Zap, k: "3840Hz", l: "Refresh Rate", d: "Flicker-free on camera for live streaming & IMAG" },
];

export default function LeyardSection() {
  return (
    <section className="relative overflow-hidden border-y border-white/10" data-testid="leyard-section">
      <div className="absolute inset-0">
        <img src={IMAGES.g3} alt="Leyard LED wall" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/85 to-[#050505]" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <motion.div {...reveal} className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#F5B841]" />
              <span className="text-xs md:text-sm tracking-[0.35em] text-[#F5B841]">POWERED BY LEYARD</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[0.95]">
              The world&apos;s leading LED,
              <br />
              <span className="text-[#F5B841]">now in the Telugu states.</span>
            </h2>
            <p className="text-white/60 text-lg font-light mt-7 max-w-xl">
              Our screens are built on <span className="text-white font-medium">Leyard</span> — an internationally
              acclaimed brand trusted on the world&apos;s biggest stages. We are proud to be the{" "}
              <span className="text-white font-medium">first to bring Leyard LED technology to Telangana &amp; Andhra
              Pradesh</span>, delivering visuals that were never possible here before.
            </p>
          </motion.div>

          <motion.div
            {...reveal}
            className="md:col-span-5 md:justify-self-end w-full md:max-w-sm border border-white/10 bg-white p-8 flex flex-col items-center"
            data-testid="leyard-logo-card"
          >
            <img src="/events/leyard-logo.jpg" alt="Leyard brand logo" className="w-full max-w-[240px] object-contain" />
            <p className="text-neutral-500 text-sm mt-5 text-center tracking-wide">
              A first for the two states — exclusively with Bhaaskar Productions.
            </p>
          </motion.div>
        </div>

        {/* Pixel quality showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-16 items-stretch">
          <motion.figure
            {...reveal}
            data-testid="led-panel-visual"
            className="relative overflow-hidden border border-white/10 min-h-[320px] group"
          >
            <img
              src="/events/led-panel.jpg"
              alt="Leyard P4.8 fine-pitch LED panel"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            <div className="absolute top-5 left-5 flex items-center gap-2 bg-[#F5B841] text-black px-3 py-1.5 font-display font-semibold tracking-wide">
              <ScanLine className="w-4 h-4" /> P4.8 FINE PITCH
            </div>
            <figcaption className="absolute bottom-5 left-5 right-5 text-white/85 text-sm">
              Leyard P4.8 modular LED panel — dense pixel grid for close-up clarity.
            </figcaption>
          </motion.figure>

          <div className="grid grid-cols-1 gap-px bg-white/10 border border-white/10">
            {specs.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.l}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  data-testid={`led-spec-${i}`}
                  className="bg-[#050505] p-7 flex items-center gap-5"
                >
                  <Icon className="w-7 h-7 text-[#F5B841] shrink-0" />
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-3xl text-white tracking-tight">{s.k}</span>
                      <span className="text-white/45 text-sm">{s.l}</span>
                    </div>
                    <p className="text-white/50 text-sm mt-1">{s.d}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-6">
          {badges.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                data-testid={`leyard-badge-${i}`}
                className="bg-[#050505] p-8"
              >
                <Icon className="w-7 h-7 text-[#F5B841]" />
                <h3 className="font-display text-xl mt-5 tracking-tight">{b.t}</h3>
                <p className="text-white/50 text-sm mt-2">{b.d}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
